import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import isEmpty from 'lodash/isEmpty';

import { asNumber } from 'react-jsonschema-form/lib/utils';

import { withStyles } from 'material-ui/styles';
import Input, { InputLabel } from 'material-ui/Input';
import { FormControl } from 'material-ui/Form';
import Select from 'material-ui/Select';

const styles = theme => ({
  withoutLabel: {
    marginTop: theme.spacing.unit,
  },
});

/**
 * This is a silly limitation in the DOM where option change event values are
 * always retrieved as strings.
 */
const processValue = ({ type, items }, value) => {
  if (value === '') {
    return value;
  } else if (
    type === 'array' &&
    items &&
    ['number', 'integer'].includes(items.type)
  ) {
    return value.map(asNumber);
  } else if (type === 'boolean') {
    return value === 'true';
  } else if (type === 'number') {
    return asNumber(value);
  }
  return value;
};

const getValue = (event, multiple) => {
  if (multiple) {
    return [].slice
      .call(event.target.options)
      .filter(o => o.selected)
      .map(o => o.value);
  } else {
    return event.target.value;
  }
};

const SelectWidget = props => {
  const {
    classes,
    schema,
    uiSchema,
    errorSchema,
    formContext,
    name,
    id,
    options,
    value,
    required,
    disabled,
    readonly,
    multiple,
    autofocus,
    onChange,
    onBlur,
    onFocus,
    placeholder,
  } = props;

  const { validatorKey } = options;
  const hasError = !isEmpty(errorSchema);
  const formControlState = formContext.formControlStates[validatorKey];
  const shouldShowError =
    hasError && (formControlState.dirty || formContext.shouldStrictValidate);
  const { enumOptions } = options;
  const emptyValue = multiple ? [] : '';
  const title =
    uiSchema && uiSchema['ui:title']
      ? uiSchema['ui:title']
      : schema.title === undefined ? name : schema.title;

  const displayLabel = schema.title !== undefined;

  return (
    <FormControl
      disabled={disabled || formContext.formStatus !== 'editable'}
      margin="normal"
      error={shouldShowError}
      className={classnames({
        [classes.withoutLabel]: !displayLabel,
      })}
    >
      {title && (
        <InputLabel shrink htmlFor={`${id}-native-input`}>
          {title}
        </InputLabel>
      )}
      <Select
        native
        id={id}
        multiple={multiple}
        value={typeof value === 'undefined' ? emptyValue : value}
        required={required}
        disabled={disabled || readonly || formContext.formStatus !== 'editable'}
        autoFocus={autofocus}
        input={<Input id={`${id}-native-input`} />}
        onBlur={
          onBlur &&
          (event => {
            const newValue = getValue(event, multiple);
            onBlur(id, processValue(schema, newValue));
          })
        }
        onFocus={
          onFocus &&
          (event => {
            const newValue = getValue(event, multiple);
            onFocus(id, processValue(schema, newValue));
          })
        }
        onChange={event => {
          const newValue = getValue(event, multiple);
          onChange(processValue(schema, newValue));
        }}
      >
        {!multiple &&
          !schema.default && <option value="">{placeholder}</option>}
        {enumOptions.map(({ value, label }, i) => {
          return (
            <option key={i} value={value}>
              {label}
            </option>
          );
        })}
      </Select>
    </FormControl>
  );
};

SelectWidget.defaultProps = {
  autofocus: false,
};

SelectWidget.propTypes = {
  classes: PropTypes.object.isRequired,
  schema: PropTypes.object.isRequired,
  uiSchema: PropTypes.object,
  errorSchema: PropTypes.object.isRequired,
  formContext: PropTypes.object.isRequired,
  id: PropTypes.string.isRequired,
  name: PropTypes.string,
  placeholder: PropTypes.string,
  options: PropTypes.shape({
    enumOptions: PropTypes.array,
  }).isRequired,
  value: PropTypes.any,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  readonly: PropTypes.bool,
  multiple: PropTypes.bool,
  autofocus: PropTypes.bool,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
};

export default withStyles(styles)(SelectWidget);



// WEBPACK FOOTER //
// ./src/modules/common/components/form/widgets/SelectWidget.js